// @flow
import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import compact from 'lodash/compact'
import useKeyboardShortcut from 'use-keyboard-shortcut'

import { colors } from '../styles'
import { actions, DesignContext } from '../store'
import AutoSave from './AutoSave'
import { Toolbar } from './Toolbar'
import LayoutSingleBoard from './LayoutSingleBoard'

import type { DesignBoardItem, Slot } from '../types'

type Props = {
  items: Array<DesignBoardItem>,
  slots: Array<Slot>,
  onSaveItems: Function,
  onSaveItem: Function,
  onRemoveItem: Function,
  onChangeBoard: Function,
  setCurrentDimensions: Function,
  dimensions: Object,
  editable: boolean,
  activeSlotId: string,
  setActiveSlotId: Function,
  setLandingSpot: Function,
  landingSpot: Object,
  fontLoaded: boolean,
  printMode: boolean,
  isCollection: boolean,
}

// const attributeKeys = [ 'width', 'height', 'left', 'top', 'scaleX', 'scaleY', 'text', 'textLines', 'zoomX', 'zoomY' ]

function DesignBoard (props: Props) {
  const { dispatch, getCurrentBoard, state } = useContext(DesignContext)
  const { config: { disclaimer }, currentBoardIndex, community, lot, externalLabel } = state

  const {
    items,
    slots,
    dimensions,
    editable,
    activeSlotId,
    setActiveSlotId,
    landingSpot,
    setLandingSpot,
    fontLoaded,
    printMode,
    isCollection
  } = props
  const [ activeItemId, setActiveItemId ] = useState(null)

  // console.log('DesignBoard', props, activeSlotId, state)
  const deselect = () => {
    setActiveItemId(null)
    setActiveSlotId(null)
  }

  useEffect(deselect, [ currentBoardIndex ])

  let board = getCurrentBoard()
  // console.log('board', board)
  const titleParts = isCollection ? [ community?.title, externalLabel ] : [ community?.title, lot?.label ]
  const title = compact(titleParts).join(' – ')
  const subtitle = board?.label
  const subtitle2 = board?.label2 ? board.label2 : null
  const divider = board?.divider
  const logo = community?.logo
  const activeItem = board?.items.find(i => i.id === activeItemId)

  const checkDeselect = (e) => {
    // console.log('deselecting?', e, e.target, e.target.getStage())
    // deselect when clicked on empty area
    const clickedOnEmpty = e.target === e.target.getStage()
    if (clickedOnEmpty) {
      deselect()
      setLandingSpot(null)
    }
  }

  function handleKeyUp (evt) {
    // console.log('DesignBoard.keyUp', evt.key, evt.keyCode, evt)
    if (evt.keyCode === 27) {
      deselect()
    } else if (evt.keyCode === 46 /* DEL */ || evt.keyCode === 8 /* BACKSPACE */) {
      dispatch({ type: actions.REMOVE_ITEM, item: activeItem })
      setActiveItemId(null)
    }
  }

  function updateActiveItem (option) {
    // console.log('updating active item', activeItem, option)
    switch (option.action) {
      case actions.REMOVE_ITEM:
        dispatch({ type: actions.REMOVE_ITEM, item: activeItem })
        setActiveItemId(null)
        break
      case actions.UPDATE_ITEM_ATTR:
      case actions.REORDER_ITEM:
        dispatch({ ...option, type: option.action, item: activeItem })
        break

      default:
        console.warn('unknown action received', option)
    }
  }

  function handleRemoveItem () {
    dispatch({ type: actions.REMOVE_ITEM, item: activeItem })
    setActiveItemId(null)
  }

  useKeyboardShortcut([ 'Escape' ], deselect, { overrideSystem: true })
  useKeyboardShortcut([ 'Backspace' ], handleRemoveItem, { overrideSystem: true })
  useKeyboardShortcut([ 'Delete' ], handleRemoveItem, { overrideSystem: true })

  // useEffect(() => {
  //   // console.log('setting up listener')
  //   window.addEventListener('keyup', handleKeyUp)
  //
  //   return () => {
  //     window.removeEventListener('keyup', handleKeyUp)
  //   }
  // })

  const board3attrs = { subtitle2, divider }
  const layoutAttrs = {
    dimensions,
    title,
    subtitle,
    logo,
    disclaimer,
    items,
    fontLoaded,
    slots: editable ? slots : [],
    isItemSelected: (item) => item.id === activeItemId,
    onSelectItem: (item) => setActiveItemId(item.id),
    isSlotSelected: (slot) => slot.id === activeSlotId,
    onSelectSlot: (slot) => {
      setActiveItemId(null)
      setActiveSlotId(slot.id)
    },
    onDeselect: checkDeselect,
    onChangeItem: (item, attrs) => {
      dispatch({ type: actions.UPDATE_ITEM, item: { ...item, attributes: { ...item.attributes, ...attrs } } })
    },
    onChangeDivider: (attrs) => {
      dispatch({ type: actions.UPDATE_BOARD, board: { divider: { ...divider, ...attrs } } })
    }
    // isItemSelected={} onChangeItem={} onSelectItem={} onDeselectItem={}
  }

  return (
    <>
      {printMode || <AutoSave data={state} />}

      {printMode || (
        <DesignBoardWrapper id="canvas" className="no-print">
          <LayoutSingleBoard {...layoutAttrs} editable={editable} boardIndex={currentBoardIndex}
                             {...(currentBoardIndex === 2) ? board3attrs : {}}
                             landingSpot={(activeItemId || activeSlotId) ? null : landingSpot} />

          {(editable && activeItemId) && (
            <ToolbarWrapper style={{ ...dimensions }}>
              <StyledToolbar><Toolbar item={activeItem} onClick={updateActiveItem} /></StyledToolbar>
            </ToolbarWrapper>
          )}
        </DesignBoardWrapper>)
      }

      <PrintGroup className="x-print" data-print-mode={printMode}>
        {state.boards.map((board, i) => {
          // console.log('printing board', i, board.items.length, board.items, board)
          if (board.items.length === 0) { return null }
          const subtitle = board?.label
          const other = (i === 2) ? board3attrs : {}
          return (
            <LayoutSingleBoard key={subtitle} boardIndex={i} {...layoutAttrs} {...other} subtitle={subtitle} items={board.items} editable={false} />
          )
        })}
      </PrintGroup>
    </>
  )
}

// DesignBoard.whyDidYouRender = true

DesignBoard.defaultProps = {
  slots: []
}

export default DesignBoard

const DesignBoardWrapper = styled.section`
  width: 100%;
  height: 100%;
  background: #444;
  overflow: hidden;
  padding: 10px;
  position: relative;
  
  @media print {
    padding: 0;
  }
`

const PrintGroup = styled.div`
  display: none;
  box-sizing: border-box;
  
  &[data-print-mode="true"] [data-page] {
    display: block;
    width: 11in !important;
    height: 8.5in !important;    
    box-sizing: border-box;
    display: block;
  }
  
  @media print {
    display: block;
    [data-page] {
      page-break-after: always;
      width: 11in !important;
      height: 8.5in !important;    
      box-sizing: border-box;
    }
  }
`

const ToolbarWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  margin: 10px;
  width: 100%;
  height: 100%;
`

const StyledToolbar = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #fff;
  padding: 0.5rem;
  border: 1px solid ${colors.darkOrange};
  box-shadow: 0 0 20px ${colors.darkGray};
  z-index: 100;
  @media print { display: none; }
`
